/**
 * Wellness alerts: is anyone still using this kitchen?
 *
 * A care operator watching forty residents needs the quieter signal the alarm
 * feed never gives. An alarm fires when something goes wrong at the stove; this
 * fires when nothing happens at all, which for an older adult living alone can
 * matter just as much.
 *
 * Everything here is pure and React-free so both feeds and the tests use the
 * identical logic. The derivation deliberately returns the same shape the API
 * will return once the backend sweep exists (see cts_api REQ-WELL-17+), so
 * swapping the source later does not touch the feeds.
 *
 * WHY DERIVE THIS CLIENT-SIDE AT ALL: the sensors table already walks every
 * sensor's presence and sessions to build its rows, and already renders "Last
 * Session"/"Last Presence" columns whose tooltips say "consider a wellness
 * check". The signal is on screen; what was missing is that it was not in the
 * triage feed and not actionable. Deriving from data already fetched means the
 * feed works today, with none of the wellness AWS infrastructure deployed.
 *
 * THE RULE THAT MATTERS MOST: a sensor that is offline or out of contact is not
 * a quiet kitchen. It is a kitchen we cannot see. Reporting the two the same way
 * is wrong in both directions, so `applyCoverageRule` below drops activity
 * claims whenever coverage is in doubt. The backend enforces the same rule when
 * it raises alerts (cts_api REQ-WELL-14); this is the client half of it.
 */

import type { AttentionIssueKind } from "./digestUtils";

/** The wellness kinds, as distinct from the alarm-derived kinds. */
export const WELLNESS_KINDS = ["notSeen", "noCooking"] as const;

export type WellnessKind = (typeof WELLNESS_KINDS)[number];

export function isWellnessKind(kind: AttentionIssueKind): boolean {
  return (WELLNESS_KINDS as readonly string[]).includes(kind);
}

/**
 * Default thresholds, in whole days.
 *
 * Hardcoded until the settings screen and the per-caregiver defaults land.
 * These match what the phone app ships so the two products do not disagree
 * about the same resident.
 */
export const DEFAULT_NOT_SEEN_DAYS = 2;
export const DEFAULT_NO_COOKING_DAYS = 3;

export type WellnessThresholds = {
  notSeenDays: number;
  noCookingDays: number;
};

export const DEFAULT_THRESHOLDS: WellnessThresholds = {
  notSeenDays: DEFAULT_NOT_SEEN_DAYS,
  noCookingDays: DEFAULT_NO_COOKING_DAYS,
};

/**
 * What the feed needs to know about one resident. Mirrors the server's alert
 * record closely enough that the source can be swapped without touching
 * callers.
 */
export type WellnessAlert = {
  scsName: string;
  kind: WellnessKind;
  /** Whole days since the relevant activity, at the time of derivation. */
  days: number;
  /** The threshold that was crossed, so the chip can explain itself. */
  thresholdDays: number;
};

/**
 * Signals for one sensor. Timestamps are epoch millis, matching what the
 * sensors table already holds; `null` means never recorded.
 */
export type WellnessSignals = {
  scsName: string;
  lastPresenceAt: number | null;
  lastCookedAt: number | null;
  /** False when the sensor is offline or otherwise out of contact. */
  inContact: boolean;
};

/**
 * Whole days between two instants, floored.
 *
 * Deliberately not calendar days. The server counts local calendar days because
 * it knows the subscriber's timezone; the browser does not know the RESIDENT's
 * timezone, only its own, and a care operator may well be in a different one
 * from the property. Elapsed days is the honest approximation here, and it is
 * conservative: it can only ever under-report, never raise a flag early.
 */
export function wholeDaysSince(at: number | null, now: number): number | null {
  if (at == null) return null;
  const ms = now - at;
  if (ms < 0) return 0;
  return Math.floor(ms / 86_400_000);
}

/**
 * Derive the wellness alerts for one sensor.
 *
 * Returns an empty list, never a partial claim, when the sensor is out of
 * contact: we cannot tell a resident who has stopped cooking from a sensor that
 * has stopped reporting, and guessing is the one thing this feature must not
 * do. The caller surfaces that as a coverage issue instead.
 */
export function deriveWellnessAlerts(
  signals: WellnessSignals,
  thresholds: WellnessThresholds = DEFAULT_THRESHOLDS,
  now: number = Date.now()
): WellnessAlert[] {
  if (!signals.inContact) return [];

  const alerts: WellnessAlert[] = [];

  // "Seen" is presence OR cooking, whichever is more recent. Someone eating
  // delivered meals is plainly present even though they have not cooked, so
  // requiring both would flag people who are perfectly fine.
  const lastSeen = maxOrNull(signals.lastPresenceAt, signals.lastCookedAt);
  const seenDays = wholeDaysSince(lastSeen, now);

  // A sensor with no activity on record never fires. Without a baseline there
  // is no "stopped", and a sensor installed yesterday would otherwise announce
  // that the resident had stopped cooking before they had a chance to start.
  if (seenDays != null && seenDays >= thresholds.notSeenDays) {
    alerts.push({
      scsName: signals.scsName,
      kind: "notSeen",
      days: seenDays,
      thresholdDays: thresholds.notSeenDays,
    });
  }

  const cookedDays = wholeDaysSince(signals.lastCookedAt, now);
  if (cookedDays != null && cookedDays >= thresholds.noCookingDays) {
    alerts.push({
      scsName: signals.scsName,
      kind: "noCooking",
      days: cookedDays,
      thresholdDays: thresholds.noCookingDays,
    });
  }

  return alerts;
}

/** Chip text for one alert. Short: the feed shows several per row. */
export function wellnessChipText(alert: WellnessAlert): string {
  const days = alert.days === 1 ? "1 day" : `${alert.days} days`;
  return alert.kind === "notSeen"
    ? `Not seen ${days}`
    : `No cooking ${days}`;
}

/**
 * Drop activity claims when coverage is in doubt.
 *
 * A row carrying `offline` cannot also honestly carry "not seen for 3 days":
 * the sensor has not been able to see anything. Keeping both would let a reader
 * conclude something about the resident from what is actually a fact about the
 * hardware.
 *
 * Applied at render time rather than at derivation because a row's coverage
 * issues come from the alarm/connectivity side, which `deriveWellnessAlerts`
 * does not see. It is also the safety net for server-raised alerts that were
 * legitimately raised BEFORE the sensor went dark and are still open.
 */
export function applyCoverageRule<T extends { kind: AttentionIssueKind }>(
  issues: T[]
): T[] {
  const coverageInDoubt = issues.some((i) => i.kind === "offline");
  if (!coverageInDoubt) return issues;
  return issues.filter((i) => !isWellnessKind(i.kind));
}

function maxOrNull(a: number | null, b: number | null): number | null {
  if (a == null) return b;
  if (b == null) return a;
  return a > b ? a : b;
}
